import React, { MouseEventHandler, FormEventHandler } from 'react';
import {
  Button as MUIV5Button,
  SxProps,
  ButtonProps as MUIV5ButtonProps,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';

import { ButtonTheme, defaultTheme } from './buttonTheme';

export enum ButtonType {
  BUTTON = 'button',
  SUBMIT = 'submit',
}

export type ButtonProps = MUIV5ButtonProps & {
  /**
   * Type of button to use. Defaults to 'button'
   * @deprecated use type prop instead
   */
  buttonType?: ButtonType;
  /**
   * String of class(es) to be added to the button.
   * This should only be used for very particular styling. Try and utilize the theme prop first.
   * Classes are postpended after button class
   */
  className?: string;
  /**
   * The component used for the root node. Either a string to use a HTML element or a component.
   */
  component?: any;
  /**
   * IDs for testing the button
   */
  dataTestId?: string;
  /**
   * Whether or not the button is disabled
   */
  disabled?: boolean;
  /**
   * Form id associated with button. Used for submitting forms.
   */
  form?: string;
  /**
   * If set to true -> button width will be set to 100% of it's parent container.
   */
  isFullWidth?: boolean;
  /**
   * Whether or not to display a loading spinner.
   * Text and icon will not display while loading is true
   */
  loading?: boolean;
  /**
   * Functionality to execute when the button is clicked
   */
  onClick?: MouseEventHandler<HTMLAnchorElement> | FormEventHandler<HTMLInputElement>;
  /**
   * Text to display inside the button
   */
  text?: string;
  /**
   * Extra props to hand down to the button text.
   * Conicide with the Typography component
   * @deprecated
   */
  // eslint-disable-next-line react/no-unused-prop-types
  textProps?: unknown;
  /**
   * Provided theme for button coloring/cobranding. See Theme type. Can also be a key of the defaultTheme object.
   */
  theme?: ButtonTheme | keyof typeof defaultTheme;
  /**
   * Variant to use.
   */
  variant?: 'contained' | 'outlined' | 'text';
  /**
   * Extra props to hand down to the button.
   */
  sx?: SxProps;
  /**
   * The path to navigate to when the button is clicked. Only used if component is Link.
   */
  to?: string;
};

const getStyles = (
  theme: ButtonTheme,
  variant?: 'contained' | 'outlined' | 'text',
  isFullWidth?: boolean,
  sx?: SxProps,
): SxProps => {
  if (variant === 'text') {
    return {
      ...(isFullWidth && { width: '100%' }),
      textTransform: 'none',
      transition: 'none',
      px: 2,
      py: 1,
      '&:disabled': {
        '& p, & svg': {
          color: theme.disabled.backgroundColor,
        },
      },
      '& > *': {
        color: theme.backgroundColor,
      },
      ...sx,
    };
  }

  return {
    ...(isFullWidth && { width: '100%' }),
    backgroundColor: theme.backgroundColor,
    border: '1px solid',
    borderColor: theme.borderColor,
    borderRadius: 100,
    textTransform: 'none',
    transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
    px: 1,
    py: 1,
    height: '50px',
    boxShadow: 'none',
    ...sx,
    '&:hover': {
      boxShadow: 'none',
      backgroundColor: theme.hover.backgroundColor,
      borderColor: theme.hover.borderColor,
      '& p, & svg': {
        color: theme.hover.childColor,
      },
      ...(sx && { ...sx['&:hover'] }),
    },
    '&:disabled': {
      backgroundColor: theme.disabled.backgroundColor,
      borderColor: theme.disabled.borderColor,
      '& p, & svg': {
        color: theme.disabled.childColor,
      },
      ...(sx && { ...sx['&:disabled'] }),
    },
    '& .MuiTypography-root': {
      transition: 'color 0.1s ease-in-out',
      ...(sx && { ...sx['& .MuiTypography-root'] }),
    },
    '& > *': {
      color: theme.childColor,
    },
  };
};

/**
 * Themed button to be used in place of Material UI button.
 * Can be provided with a theme of type Theme for styling and cobranding.
 * Note: This component is undergoing a refactor from MUI v4 to MUI v5.
 * @param {ButtonProps} props
 * @returns
 */
export const Button = ({
  className,
  component,
  dataTestId,
  disabled,
  form,
  loading,
  onClick,
  text,
  buttonType,
  startIcon,
  endIcon,
  style,
  theme = 'primary',
  variant = 'contained',
  isFullWidth = false,
  type = 'button',
  sx = {},
  to,
}: ButtonProps) => {
  const buttonTheme: ButtonTheme =
    typeof theme === 'string'
      ? defaultTheme[theme as keyof typeof defaultTheme]
      : (theme as ButtonTheme);

  return (
    <MUIV5Button
      className={className}
      sx={getStyles(buttonTheme, variant, isFullWidth, sx)}
      component={component}
      data-testid={dataTestId}
      disabled={disabled}
      form={form}
      onClick={onClick}
      type={buttonType ?? type}
      variant={variant}
      startIcon={startIcon}
      endIcon={endIcon}
      style={style}
      aria-label={text}
      disableRipple={variant === 'text'}
      to={to}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: 14,
            lineHeight: '21px',
            letterSpacing: '0px',
            visibility: loading ? 'hidden' : 'visible',
          }}
        >
          {text}
        </Typography>
        {loading && (
          <CircularProgress
            size={24}
            sx={{
              color: buttonTheme.childColor,
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: '-12px',
              marginLeft: '-12px',
            }}
          />
        )}
      </Box>
    </MUIV5Button>
  );
};

export default Button;
